import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { StudentModel, StudentRequest, UserRequest } from '@generativ/much-smarter-models';
import { FormService } from '../../services/form.service';
import { AuthService } from '../auth.service';
import { FeedbackService } from '../../feedback/feedback.service';
import { Intercom } from '../../intercom/intercom.service';
import { environment } from '../../../../environments/environment';
import { catchError, EMPTY, of, Subscription, switchMap, tap } from 'rxjs';
import { EntryDictionary, LogInType } from '@generativ/ms-student-types';
import { ContentfulService } from '../../../shared/contentful.service';
import { StudentService } from '../../../pages/student/student.service';
import { FeedbackComponent } from '../../feedback/feedback/feedback.component';
import { PhoneInputComponent } from '../../../shared/phone-input/phone-input.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, RouterLink, FeedbackComponent, PhoneInputComponent]
})
export class SignUpComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  signUpForm: UntypedFormGroup;
  ready: boolean;
  content: LogInType;
  destroyRef = inject(DestroyRef)

  constructor(
    public formService: FormService,
    private authService: AuthService,
    private feedbackService: FeedbackService,
    private router: Router,
    private intercom: Intercom,
    private contentfulService: ContentfulService,
    private studentService: StudentService
  ) {
    this.signUpForm = this.formService.createForm(this.formOptions);
    this.contentfulService.getStudentEntry(EntryDictionary.LogIn.logIn).then(
      (entry) => {
        this.content = new LogInType(entry);
        this.ready = true;
      }
    );
  }

  ngOnInit() {
    if (!this.authService.isAuthenticated()) {
      this.authService.getToken().subscribe(({tokenId}) => {
        if (tokenId) {
          this.authService.token = tokenId;
          localStorage.setItem('tokenId', tokenId);
        }
      });
    }

    if (this.authService.isAuthenticated() && !this.authService.currentStudent) {
      console.log('get profile');
      setTimeout(() => {
        this.getProfileSubscription();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submitForm() {
    if (this.formService.validateFormAndUpdateErrors(this.signUpForm)) {
      const student = new StudentRequest.Create();
      student.parse(this.signUpForm.value);
      student.phoneNumber = parsePhoneNumberFromString(student.phoneNumber).number;
      this.ready = false;
      this.feedbackService.cleanState();
      this.authService.signUp(student)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          const userInfo = new UserRequest.Login();
          userInfo.email = student.email;
          userInfo.password = student.password;
          return this.authService.signIn(userInfo)
            .pipe(
              tap(() => {
                this.intercom.shutdown();
                  this.intercom.boot({
                    app_id: environment.intercom.appId,
                    widget: { activator: '#intercom'},
                    email: userInfo.email
                  });
              }),
              switchMap(() => 
                this.authService.addKeapContact(this.signUpForm.controls.phoneNumber.value)
                  .pipe(
                    catchError((error) => {
                    console.error(error);
                    return of(null);
                  }))
                )
              )
          }
        )
      )
      .subscribe({
        next: () => {
        this.ready = true;
        this.authService.setCurrentStudent(new StudentModel().parse(student));
        this.router.navigate(['/signup/payment']);
      },
        error: (err) => {
          this.ready = true;
          this.feedbackService.error(err, true);
        }})
    }
  }

  private get formOptions(): Record<string, unknown> {
    // Goal scores still in form, but not linked to form in HTML.
    // If we re-add, they just need Validators.required added
    return {
      email: [
        null,
        Validators.compose([
          Validators.required,
          Validators.email
        ])
      ],
      password: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      firstName: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      phoneNumber: [
        '+1',
        Validators.compose([
          Validators.required
        ])
      ],
    };
  }

  private getProfileSubscription() {
    this.subscriptions.add(this.studentService.getProfile()
      .subscribe({
        next: student => {
          this.authService.setCurrentStudent(student);
          const redirectUrl = `/login/location-and-game`;
          this.router.navigate([redirectUrl]);
        },
        error: error => {
          this.feedbackService.error(error, true);
        }
      })
    );
  }
}
